import React, { useState, useEffect } from 'react';
import { FiAlertCircle, FiCheck, FiArrowLeft, FiEdit3 } from 'react-icons/fi';
import Input from '../../components/Input/Input';
import Select from '../../components/Select/Select';
import Button from '../../components/Button/Button';
import Card from '../../components/Card/Card';
import api from '../../api';
import './AddBook.css';

const INITIAL_FORM = {
  AccessionNo: '', title: '', author: '', category: '', subCategory: '', 
  edition: '', publisher: '', price: '', description: '', 
  year: '', classificationNo: '', bookNumber: '', pages: '', 
  volumn: '', itemType: 'General',
};

const AddBook = ({ onNavigate, showAlert, editId }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const isEdit = !!editId;

  /* ── Initial Fetch ── */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [catRes, subRes] = await Promise.all([
          api.get('/categories.php'),
          api.get('/subcategories.php')
        ]);
        const cats = catRes.data;
        const subs = subRes.data;
        setCategories(cats);
        setSubCategories(subs);

        if (isEdit) {
           const bookRes = await api.get(`/books.php?id=${editId}`);
           const book = bookRes.data;
           if (book && !book.error) {
              setForm({
                AccessionNo: book.AccessionNo,
                title: book.Title,
                author: book.Author,
                category: book.SubjectId,
                subCategory: book.SubId,
                edition: book.Edition,
                publisher: book.Publisher,
                price: book.Price,
                description: book.Description,
                year: book.Year,
                classificationNo: book.ClassificationNo,
                bookNumber: book.BookNumber,
                pages: book.Pages,
                volumn: book.Volumn,
                itemType: book.ItemType
              });
           }
        }
      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [editId, isEdit]);

  const categoryOptions = [
    { value: '', label: 'Select Category' },
    ...categories.map(c => ({ value: c.SubjectId, label: c.Name }))
  ];

  const subCategoryOptions = [
    { value: '', label: 'Select Sub-Category' },
    ...(form.category 
      ? subCategories.filter(s => s.SubjectId == form.category).map(s => ({ value: s.SubId, label: s.SubName }))
      : [])
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'category') setForm(p => ({ ...p, subCategory: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.AccessionNo.trim()) errs.AccessionNo = 'Accession Number is required';
    if (!form.title.trim())       errs.title       = 'Book title is required';
    if (!form.author.trim())      errs.author      = 'Author name is required';
    if (!form.category)           errs.category    = 'Please select a category';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const payload = {
        AccessionNo: form.AccessionNo,
        SubjectId: form.category,
        SubId: form.subCategory,
        Title: form.title,
        Author: form.author,
        Edition: form.edition,
        Publisher: form.publisher,
        Price: form.price || 0,
        Description: form.description,
        Year: form.year,
        ClassificationNo: form.classificationNo,
        BookNumber: form.bookNumber,
        Pages: form.pages,
        Volumn: form.volumn,
        ItemType: form.itemType
      };

      const method = isEdit ? api.put : api.post;
      const res = await method('/books.php', payload);
      const result = res.data;
      
      setSubmitted(true);
    } catch (err) {
      showAlert(isEdit ? "Update Failed" : "Save Failed", err.message, "confirm");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="add-book">
        <div className="page-header"><div><h2 className="page-header__title">{isEdit ? 'Update Book' : 'Add New Book'}</h2></div></div>
        <Card padding="lg" className="add-book__success">
          <div className="success-state">
            <div className="success-state__icon"><FiCheck /></div>
            <h3>Book {isEdit ? 'Updated' : 'Added'} Successfully!</h3>
            <p>"{form.title}" has been saved.</p>
            <div className="success-state__actions">
              <Button variant="primary" onClick={() => onNavigate?.('books')}>View Books Table</Button>
              {!isEdit && <Button variant="secondary" onClick={handleReset}>Add Another Book</Button>}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="add-book">
      <div className="page-header">
        <div>
          <h2 className="page-header__title">{isEdit ? 'Edit Book Details' : 'Add New Book'}</h2>
          <p className="page-header__sub">{isEdit ? `Modifying record for Accession No: ${editId}` : 'Register a new volume into the library database'}</p>
        </div>
        <Button variant="ghost" icon={<FiArrowLeft />} onClick={() => onNavigate?.('books')}>Back to List</Button>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="add-book__grid">
          <div className="add-book__main">
            <Card padding="md">
              <Card.Header><Card.Title>Identify & Title</Card.Title></Card.Header>
              <div className="form-grid form-grid--2">
                <Input
                  id="AccessionNo" name="AccessionNo" label="Accession No"
                  placeholder="e.g. G1072" value={form.AccessionNo}
                  onChange={handleChange} error={errors.AccessionNo} required
                  disabled={isEdit}
                />
                <Input id="bookNumber" name="bookNumber" label="Book Number" placeholder="e.g. C35R" value={form.bookNumber} onChange={handleChange} />
                <Input id="title" name="title" label="Book Title" placeholder="Enter title" value={form.title} onChange={handleChange} error={errors.title} required className="form-grid__span-2" />
                <Input id="author" name="author" label="Author" placeholder="Author name" value={form.author} onChange={handleChange} error={errors.author} required />
                <Input id="classificationNo" name="classificationNo" label="Classification No" placeholder="e.g. 322" value={form.classificationNo} onChange={handleChange} />
              </div>
            </Card>

            <Card padding="md">
              <Card.Header><Card.Title>Categorization</Card.Title></Card.Header>
              <div className="form-grid form-grid--2">
                <Select id="category" name="category" label="Main Category" options={categoryOptions} value={form.category} onChange={handleChange} error={errors.category} required />
                <Select id="subCategory" name="subCategory" label="Sub Category" options={subCategoryOptions} value={form.subCategory} onChange={handleChange} disabled={!form.category} />
              </div>
            </Card>

            <Card padding="md">
              <Card.Header><Card.Title>Publication & Stock</Card.Title></Card.Header>
              <div className="form-grid form-grid--3">
                 <Input id="publisher" name="publisher" label="Publisher" value={form.publisher} onChange={handleChange} />
                 <Input id="edition" name="edition" label="Edition" value={form.edition} onChange={handleChange} />
                 <Input id="year" name="year" label="Year" type="number" value={form.year} onChange={handleChange} />
                 <Input id="pages" name="pages" label="Pages" type="number" value={form.pages} onChange={handleChange} />
                 <Input id="price" name="price" label="Price" type="number" value={form.price} onChange={handleChange} />
                 <Input id="volumn" name="volumn" label="Volume" value={form.volumn} onChange={handleChange} />
              </div>
            </Card>

            <Card padding="md">
              <Card.Header><Card.Title>Details</Card.Title></Card.Header>
              <div className="form-group"><label className="form-label">Description</label><textarea name="description" className="form-textarea" rows={4} value={form.description} onChange={handleChange} /></div>
              <div className="form-grid form-grid--2" style={{marginTop:'16px'}}><Input id="itemType" name="itemType" label="Item Type" value={form.itemType} onChange={handleChange} /></div>
            </Card>
          </div>

          <div className="add-book__sidebar">
            <Card padding="md" className="add-book__actions">
              <div className="form-summary-card">
                  <h4>Review Details</h4>
                  <div className="form-summary-item"><span>ID:</span> <strong>{form.AccessionNo || '—'}</strong></div>
                  <div className="form-summary-item"><span>Title:</span> <strong>{form.title || '—'}</strong></div>
              </div>
              <Button type="submit" variant="primary" fullWidth loading={loading} icon={isEdit ? <FiEdit3 /> : <FiCheck />}>{isEdit ? 'Update Book' : 'Save Book'}</Button>
              {!isEdit && <Button type="button" variant="secondary" fullWidth onClick={handleReset}>Clear All</Button>}
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddBook;
